import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, API, formatApiErrorDetail } from "../lib/api";

const INDUSTRIES = [
  "IT services",
  "Finance",
  "Retail",
  "Manufacturing",
  "Healthcare",
  "Public sector",
  "Media & entertainment",
  "Energy",
  "Telecoms",
  "Other",
];
const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

export default function OrgSettings() {
  const { user, organization, setOrganization } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAgency = organization?.type === "agency";

  const [form, setForm] = useState(null);
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!organization) return;
    setForm({
      name: organization.name || "",
      description: organization.description || "",
      website: organization.website || "",
      industry: organization.industry || "",
      size: organization.size || "",
      siret: organization.siret || "",
      specialties: organization.specialties || [],
      logo_url: organization.logo_url || null,
    });
  }, [organization]);

  const logoSrc = useMemo(() => {
    if (!form?.logo_url) return null;
    if (form.logo_url.startsWith("http")) return form.logo_url;
    return `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`;
  }, [form?.logo_url]);

  const set = (k) => (e) =>
    setForm((f) =>
      f ? { ...f, [k]: e.target ? e.target.value : e } : f
    );

  const addSpec = (raw) => {
    const v = (raw || "").replace(/^[,\s]+|[,\s]+$/g, "").trim();
    if (!v) return;
    setForm((f) =>
      !f ? f : f.specialties.includes(v) ? f : { ...f, specialties: [...f.specialties, v] }
    );
    setSpecialtyInput("");
  };
  const onSpecKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSpec(specialtyInput);
    } else if (e.key === "Backspace" && !specialtyInput && form && form.specialties.length) {
      setForm((f) => (f ? { ...f, specialties: f.specialties.slice(0, -1) } : f));
    }
  };
  const removeSpec = (s) =>
    setForm((f) => (f ? { ...f, specialties: f.specialties.filter((x) => x !== s) } : f));

  const onSave = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form) return;
      setSaving(true);
      try {
        const payload = {
          name: form.name,
          description: form.description || null,
          website: form.website || null,
        };
        if (isAgency) {
          payload.specialties = form.specialties;
          payload.siret = form.siret || null;
        } else {
          payload.industry = form.industry || null;
          payload.size = form.size || null;
        }
        const { data } = await api.patch("/orgs/me", payload);
        setOrganization(data);
        setForm((f) => ({ ...f, ...data }));
        toast.success(t("settings.saved_toast"));
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
        );
      } finally {
        setSaving(false);
      }
    },
    [form, isAgency, setOrganization, t]
  );

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error(t("settings.logo_error_type"));
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("settings.logo_error_size"));
      e.target.value = "";
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/orgs/me/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrganization(data);
      setForm((f) => (f ? { ...f, logo_url: data.logo_url } : f));
      toast.success(t("settings.logo_uploaded_toast"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const previewPath = organization
    ? isAgency
      ? `/agencies/${organization.id}`
      : `/employers/${organization.id}`
    : "/dashboard";

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 text-center text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="org-settings-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {t(`dashboard.role_badge_${user?.role}`)}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("settings.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {isAgency ? t("settings.subtitle_agency") : t("settings.subtitle_employer")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(previewPath)}
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
            data-testid="settings-preview-btn"
          >
            {t("settings.preview_cta")}
          </button>
        </div>

        {/* Logo block */}
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">{t("settings.logo_title")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("settings.logo_help")}</p>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50" data-testid="settings-logo-preview">
              {logoSrc ? (
                <img src={logoSrc} alt="Organization logo" className="h-full w-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-slate-300">
                  {(form.name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onLogo}
                className="hidden"
                data-testid="settings-logo-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingLogo}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="settings-logo-upload-btn"
              >
                {uploadingLogo ? t("common.loading") : t("settings.logo_upload")}
              </button>
              <p className="mt-1 text-xs text-slate-500">{t("settings.logo_constraints")}</p>
            </div>
          </div>
        </section>

        <form onSubmit={onSave} className="space-y-6" data-testid="settings-form">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">
              {isAgency ? t("settings.agency_details") : t("settings.employer_details")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField label={t("settings.name")} value={form.name} onChange={set("name")} required testId="settings-name" className="md:col-span-2" />
              <TextField label={t("settings.website")} value={form.website} onChange={set("website")} placeholder="https://" testId="settings-website" className="md:col-span-2" />

              {isAgency ? (
                <>
                  <TextField label={t("auth.siret")} value={form.siret} onChange={set("siret")} testId="settings-siret" />
                  <div className="md:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      {t("auth.specialties")}
                    </label>
                    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                      {form.specialties.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700" data-testid={`settings-spec-${s}`}>
                          {s}
                          <button type="button" onClick={() => removeSpec(s)} className="text-indigo-500 hover:text-indigo-700">×</button>
                        </span>
                      ))}
                      <input
                        value={specialtyInput}
                        onChange={(e) => setSpecialtyInput(e.target.value)}
                        onKeyDown={onSpecKey}
                        onBlur={() => addSpec(specialtyInput)}
                        placeholder={t("auth.specialty_placeholder")}
                        className="min-w-[10ch] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0"
                        data-testid="settings-specialty-input"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <SelectField label={t("settings.industry")} value={form.industry || ""} onChange={set("industry")} testId="settings-industry">
                    <option value="">{t("settings.select_placeholder")}</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </SelectField>
                  <SelectField label={t("settings.size")} value={form.size || ""} onChange={set("size")} testId="settings-size">
                    <option value="">{t("settings.select_placeholder")}</option>
                    {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </SelectField>
                </>
              )}

              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("settings.description")}</label>
                <textarea
                  rows={5}
                  value={form.description || ""}
                  onChange={set("description")}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="settings-description"
                  placeholder={isAgency ? t("settings.description_placeholder_agency") : t("settings.description_placeholder_employer")}
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
              data-testid="settings-submit-btn"
            >
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testId, className = "", placeholder }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, children, testId }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <select value={value} onChange={onChange} data-testid={testId} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20">
        {children}
      </select>
    </div>
  );
}
